// Inline booking: pick a day and a time, then a short form, booked without leaving the page.
// <BookingWidget />          wide card (intro left, calendar right)
// <BookingWidget compact />  slim card for page ends and narrow columns
// <BookingWidget compact collapsed />  a single "Pick a time" row that opens into the slim card
// tone: 'light' | 'dark'. Inside an .on-dark section the dark tone is applied automatically.
import React, { useEffect, useRef, useState } from 'react';
import { booking } from '../content';
import { Badge, cx, Swipe } from '../ui';
import { ApiError, CONTACT_EMAIL, companyFromEmail, fmtDay, fmtTime, isEmail, isFreeMail, tzCity, useBooking, useBookingForm, type BookingForm } from './useBooking';
import {
  Alert,
  AutoHeight,
  Confirmation,
  DateStrip,
  Honeypot,
  IconAlert,
  IconArrow,
  IconCheck,
  NoSlots,
  PickerSkeleton,
  Spinner,
  TextField,
  TimeGrid,
  TzLine,
} from './parts';

type Props = { compact?: boolean; source?: string; tone?: 'light' | 'dark'; collapsed?: boolean; className?: string };
type Errs = Partial<Record<'email' | 'firstName' | 'lastName' | 'company', string>>;

export function BookingWidget({ compact = false, source, tone, collapsed = false, className }: Props) {
  const [open, setOpen] = useState(!collapsed);
  if (!open)
    return (
      <div className={cx('bk', className)} data-tone={tone}>
        <div className="bk-card flex flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Badge size={38} />
            <div className="leading-tight">
              <div className="bk-h text-[16px]">Book 30 minutes</div>
              <div className="text-[13.5px] bk-muted">Video call. No slides.</div>
            </div>
          </div>
          <button type="button" className="bk-btn go max-sm:w-full" onClick={() => setOpen(true)} aria-expanded={false}>
            Pick a time <IconArrow />
          </button>
        </div>
      </div>
    );
  return <Widget compact={compact} source={source} tone={tone} className={className} autoFocus={collapsed} />;
}

function Widget({ compact, source, tone, className, autoFocus }: Props & { autoFocus?: boolean }) {
  const { form, set: setField } = useBookingForm();
  const bk = useBooking({ source: source || 'widget' });
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errs>({});
  const set = <K extends keyof BookingForm>(k: K, v: BookingForm[K]) => {
    setField(k, v);
    setErrors((e) => (k in e ? { ...e, [k]: undefined } : e));
  };
  const [banner, setBanner] = useState<React.ReactNode>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const focused = useRef(false);

  const dates = bk.availability?.dates ?? [];
  const day = dates.find((d) => d.date === date);

  useEffect(() => {
    // only after the visitor opened the collapsed row, and only once
    if (!autoFocus || focused.current || !bk.availability) return;
    focused.current = true;
    rootRef.current?.querySelector<HTMLElement>('.bk-sday, .bk-time')?.focus({ preventScroll: true });
  }, [autoFocus, bk.availability]);

  useEffect(() => {
    if (!bk.availability) return;
    const ds = bk.availability.dates;
    const withSlot = slot ? ds.find((d) => d.slots.some((s) => s.start === slot)) : undefined;
    if (slot && !withSlot) setSlot(null);
    if (withSlot) setDate(withSlot.date);
    else if (!date || !ds.some((d) => d.date === date)) setDate(ds[0]?.date ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.availability]);

  const pick = (s: string) => {
    const first = !slot;
    setSlot(s);
    setBanner(null);
    if (first)
      requestAnimationFrame(() => {
        const el = formRef.current?.querySelector<HTMLInputElement>('input:not([tabindex="-1"])');
        el?.focus({ preventScroll: true });
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slot) return;
    const er: Errs = {};
    if (!isEmail(form.email)) er.email = form.email ? 'That email does not look right' : 'Enter your email';
    if (!form.firstName.trim()) er.firstName = 'Enter your first name';
    if (!form.lastName.trim()) er.lastName = 'Enter your last name';
    if (!form.company.trim()) er.company = 'Enter your company';
    setErrors(er);
    if (Object.keys(er).length) {
      formRef.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
      return;
    }
    setBanner(null);
    try {
      await bk.book(slot, form);
    } catch (x) {
      const err = x as ApiError;
      if (err.code === 'VALIDATION' && err.fields) setErrors(err.fields as Errs);
      if (err.code === 'SLOT_UNAVAILABLE') {
        setSlot(null);
        setBanner('Someone just took that time. The calendar is refreshed, pick another. Your details are kept.');
      } else
        setBanner(
          <>
            {err.message}{' '}
            {err.code !== 'VALIDATION' && (
              <a className="bk-link" href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL}
              </a>
            )}
          </>
        );
    }
  };

  const free = isEmail(form.email) && isFreeMail(form.email);

  const picker = (
    <div>
      <TzLine tz={bk.tz} detected={bk.detected} onChange={bk.setTz} className="mb-4" />
      {bk.loading && !bk.availability ? (
        <PickerSkeleton compact />
      ) : bk.error && !bk.availability ? (
        <Alert>
          {bk.error}{' '}
          <button type="button" className="bk-link" onClick={bk.refresh}>
            Try again
          </button>
        </Alert>
      ) : dates.length === 0 ? (
        <NoSlots />
      ) : (
        <div className={cx(bk.loading && 'opacity-60')}>
          <DateStrip dates={dates} selected={date} onSelect={setDate} />
          {day && (
            <div className="mt-5">
              <div className="mb-2.5 text-[14px] font-semibold" style={{ color: 'var(--bk-fg)' }}>
                {fmtDay(day.slots[0].start, bk.tz, true)}
              </div>
              <TimeGrid key={day.date} slots={day.slots} tz={bk.tz} selected={slot} onSelect={pick} label={`Times on ${day.label}`} limit={8} cols4 />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const shortForm = (
    <AutoHeight>
      {slot ? (
        <div ref={formRef} className="bk-step-in pt-6">
          <div className="border-t pt-6" style={{ borderColor: 'var(--bk-line)' }}>
          <form noValidate onSubmit={submit}>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[15.5px] font-semibold" style={{ color: 'var(--bk-fg)' }}>
                <span style={{ color: 'var(--bk-accent)' }}>
                  <IconCheck size={16} />
                </span>
                {fmtDay(slot, bk.tz)}, {fmtTime(slot, bk.tz)} <span className="font-normal bk-muted">({tzCity(bk.tz)})</span>
              </div>
              <button type="button" className="bk-link text-[13.5px]" onClick={() => setSlot(null)}>
                Change time
              </button>
            </div>
            <Honeypot value={form.website} onChange={(v) => set('website', v)} />
            <div className={cx('grid gap-4', !compact && 'sm:grid-cols-2')}>
              <TextField
                className={cx(!compact && 'sm:col-span-2')}
                label="Work email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={form.email}
                onChange={(v) => set('email', v)}
                onBlur={() => !form.company.trim() && companyFromEmail(form.email) && set('company', companyFromEmail(form.email))}
                error={errors.email}
                note={
                  free ? (
                    <div className="bk-note">
                      <IconAlert />
                      <span>Use your work email if you can, it helps us prepare.</span>
                    </div>
                  ) : null
                }
              />
              <div className="grid grid-cols-2 gap-3 sm:contents">
                <TextField label="First name" autoComplete="given-name" value={form.firstName} onChange={(v) => set('firstName', v)} error={errors.firstName} />
                <TextField label="Last name" autoComplete="family-name" value={form.lastName} onChange={(v) => set('lastName', v)} error={errors.lastName} />
              </div>
              <TextField className={cx(!compact && 'sm:col-span-2')} label="Company" autoComplete="organization" value={form.company} onChange={(v) => set('company', v)} error={errors.company} />
            </div>
            {banner && (
              <div className="mt-4">
                <Alert>{banner}</Alert>
              </div>
            )}
            <button type="submit" className="bk-btn go mt-5 w-full" aria-disabled={bk.submitting}>
              {bk.submitting ? (
                <>
                  <Spinner /> Booking
                </>
              ) : (
                <>
                  Book {fmtDay(slot, bk.tz)}, {fmtTime(slot, bk.tz)}
                </>
              )}
            </button>
            <p className="mt-3 text-center text-[12.5px] bk-muted">
              Want to tell us more first? Use the{' '}
              <a className="bk-link font-medium" href="/book-a-demo">
                full booking page
              </a>
              .
            </p>
          </form>
          </div>
        </div>
      ) : banner ? (
        <div className="mt-4">
          <Alert>{banner}</Alert>
        </div>
      ) : null}
    </AutoHeight>
  );

  const confirmation = bk.result && (
    <Confirmation
      compact
      result={bk.result}
      tz={bk.tz}
      cancelled={bk.cancelled}
      onCancel={bk.cancel}
      onRebook={() => {
        bk.reset();
        setSlot(null);
      }}
    />
  );

  if (compact)
    return (
      <div ref={rootRef} className={cx('bk', className)} data-tone={tone}>
        <div className="bk-card p-5 sm:p-7">
          <div className="mb-5 flex items-center gap-3">
            <Badge size={38} />
            <div className="leading-tight">
              <h2 className="bk-h text-[18px]">Book 30 minutes</h2>
              <div className="mt-0.5 text-[13.5px] bk-muted">Video call. No slides.</div>
            </div>
          </div>
          <AutoHeight>
            {confirmation || (
              <>
                {picker}
                {shortForm}
              </>
            )}
          </AutoHeight>
        </div>
      </div>
    );

  return (
    <div ref={rootRef} className={cx('bk', className)} data-tone={tone}>
      <div className="bk-card grid overflow-hidden lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <div className="border-b p-6 sm:p-9 lg:border-b-0 lg:border-r" style={{ borderColor: 'var(--bk-line)' }}>
          <h2 className="bk-h text-[clamp(30px,3vw,40px)] font-extrabold leading-[1.04] tracking-[-0.035em]">
            {booking.title[0]} <Swipe>{booking.title[1]}</Swipe>
          </h2>
          <p className="mt-4 text-[16px] leading-relaxed bk-muted">{booking.lede}</p>
          <ul className="mt-6 grid gap-2.5">
            {booking.expect.map((x) => (
              <li key={x} className="flex gap-2.5 text-[15px]" style={{ color: 'var(--bk-fg)' }}>
                <span className="mt-[3px]" style={{ color: 'var(--bk-accent)' }}>
                  <IconCheck size={16} />
                </span>
                {x}
              </li>
            ))}
          </ul>
        </div>
        <div className="p-6 sm:p-9">
          <AutoHeight>
            {confirmation || (
              <>
                {picker}
                {shortForm}
              </>
            )}
          </AutoHeight>
        </div>
      </div>
    </div>
  );
}

export default BookingWidget;
